import { createContext, useCallback, useContext, useState } from 'react'
import { Dialog } from 'radix-ui'
import { Button } from '@/components/ui/button'

export interface ConfirmOptions {
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (result: boolean) => void
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const confirm: ConfirmFn = useCallback((options) => {
    return new Promise<boolean>((resolve) => {
      setPending({ options, resolve })
    })
  }, [])

  const handleResult = (result: boolean) => {
    pending?.resolve(result)
    setPending(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Dialog.Root open onOpenChange={(o) => { if (!o) handleResult(false) }}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
            <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold">{pending.options.title}</Dialog.Title>
              {pending.options.description && (
                <Dialog.Description className="mt-2 text-sm text-slate-600">
                  {pending.options.description}
                </Dialog.Description>
              )}
              <div className="mt-6 flex justify-end gap-2">
                <Button variant="outline" onClick={() => handleResult(false)}>
                  {pending.options.cancelLabel ?? 'Anuluj'}
                </Button>
                <Button
                  variant={pending.options.variant === 'destructive' ? 'destructive' : 'default'}
                  onClick={() => handleResult(true)}
                  autoFocus
                >
                  {pending.options.confirmLabel ?? 'Potwierdź'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}
